import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Tooltip, TooltipAlignment } from '@repo/ui/components/Tooltip';

import Dropdown from '../Dropdown/Dropdown';
import { Option } from '../Dropdown/types';
import SelectController from '../SelectController/SelectController';
import { TooltipPlacement } from '../Tooltip/types';

import { ErrorMessage, SelectInnerWrapper, SelectWrapper } from './styles';
import { SelectProps, TypedSelectComponent } from './types';

const CLEAR_SEARCH_TEXT_TIMEOUT = 500;

const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      options,
      optionCustomStyles,
      compact = false,
      small = false,
      disabled = false,
      noExpandIcon = false,
      dropdownCustomStyles,
      error,
      activeOption,
      placeholder,
      onSelect,
      customStyles,
      dataTestId = 'select',
      renderItem,
      offsetParent,
      forceBottomDropdown,
      searchOnInput,
      closeOnSelect = true,
      minSafeMargin,
      onMouseEnterOption,
      onMouseLeaveOption,
      canDeselect,
      hideActiveLabel,
      renderExpandIcon,
      tooltipText,
      tooltipPlacement,
      onExpand,
      onClick,
      onPointerDownAway,
    },
    ref
  ) => {
    const relativeWrapperRef = useRef<HTMLDivElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const setRef = (node: HTMLDivElement): void => {
      wrapperRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const [dropdownExpanded, setDropdownExpanded] = useState(false);
    const [searchText, setSearchText] = useState<string>('');

    useEffect(() => {
      if (!searchText) {
        return;
      }

      const timer = setTimeout(() => {
        setSearchText('');
      }, CLEAR_SEARCH_TEXT_TIMEOUT);
      return (): void => clearTimeout(timer);
    }, [searchText]);

    const label = hideActiveLabel
      ? ''
      : renderItem && activeOption?.label
        ? renderItem(activeOption, true)
        : (activeOption?.label ?? placeholder ?? 'Select');

    const handleControllerPointerDown = useCallback(() => {
      const newExpendedValue = !dropdownExpanded;

      setDropdownExpanded(newExpendedValue);
      setSearchText('');

      if (newExpendedValue) {
        onExpand?.();
      }
    }, [dropdownExpanded, onExpand]);

    const handleOptionClick = useCallback(
      (option: Option): void => {
        if (onSelect) {
          if (canDeselect || option.id !== activeOption?.id) {
            onSelect(option);
          }
        }
        if (closeOnSelect) {
          setDropdownExpanded(false);
        }
      },
      [onSelect, activeOption, closeOnSelect, canDeselect]
    );

    const handlePointerDownAway = useCallback(
      (event: MouseEvent): void => {
        if (
          event.target instanceof Node &&
          wrapperRef.current?.contains(event.target)
        ) {
          return;
        }

        setDropdownExpanded(false);
        onPointerDownAway?.();
      },
      [setDropdownExpanded, onPointerDownAway]
    );

    return (
      <SelectWrapper
        ref={setRef}
        customStyles={customStyles}
        data-testid={dataTestId}
        small={small}
      >
        <SelectInnerWrapper
          ref={relativeWrapperRef}
          customStyles={customStyles}
          disabled={disabled}
          small={small}
          tabIndex={searchOnInput ? 0 : undefined}
          onClick={(e: React.MouseEvent): void => onClick?.(e)}
          onKeyDown={(e: React.KeyboardEvent): void => {
            const isServiceKey =
              e.shiftKey || e.altKey || e.ctrlKey || e.metaKey;

            if (!searchOnInput || !dropdownExpanded || isServiceKey) {
              return;
            } else if (/^[0-9a-zA-Z ]$/gi.test(e.key)) {
              setSearchText((prevValue) => prevValue + e.key);
            }
          }}
        >
          <MaybeTooltip
            hide={dropdownExpanded}
            placement={tooltipPlacement}
            text={tooltipText}
          >
            <SelectController
              alert={!!error}
              customStyles={{
                height: customStyles?.height,
                noBorder: customStyles?.noBorder,
                background: customStyles?.background,
                paddingLeft: customStyles?.paddingLeft,
                paddingRight: customStyles?.paddingRight,
                paddingTop: customStyles?.paddingTop,
                paddingBottom: customStyles?.paddingBottom,
                borderRadius: customStyles?.borderRadius,
                iconColor: customStyles?.iconColor,
                iconActiveColor: customStyles?.iconActiveColor,
                fontWeight: customStyles?.fontWeight,
                textDecoration: customStyles?.textDecoration,
                textColor: customStyles?.textColor,
                textColorDisabled: customStyles?.textColorDisabled,
                expandIconWidth: customStyles?.expandIconWidth,
                expandIconSvgHeight: customStyles?.expandIconSvgHeight,
                iconBottom: customStyles?.iconBottom,
                cursorDisabled: customStyles?.cursorDisabled,
              }}
              disabled={disabled}
              dropdownExpanded={dropdownExpanded}
              label={label}
              noExpandIcon={noExpandIcon}
              renderExpandIcon={renderExpandIcon}
              small={small}
              onPointerDown={handleControllerPointerDown}
            />
          </MaybeTooltip>
          <Dropdown
            activeOption={activeOption}
            compact={compact}
            customStyles={dropdownCustomStyles}
            dataTestId="select-dropdown"
            expanded={dropdownExpanded}
            forceBottomDropdown={forceBottomDropdown}
            minSafeMargin={minSafeMargin}
            offsetParent={offsetParent}
            optionCustomStyles={optionCustomStyles}
            options={options}
            relativeParent={relativeWrapperRef.current}
            renderItem={renderItem}
            searchText={searchText}
            small={small}
            onMouseEnterOption={(option) => {
              if (dropdownExpanded) {
                onMouseEnterOption?.(option);
              }
            }}
            onMouseLeaveOption={(option) => {
              if (dropdownExpanded) {
                onMouseLeaveOption?.(option);
              }
            }}
            onOptionClick={handleOptionClick}
            onPointerDownAway={handlePointerDownAway}
          />
        </SelectInnerWrapper>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </SelectWrapper>
    );
  }
);

// @ts-expect-error: memo + forwardRef clear all type data
const exportedSelect: TypedSelectComponent = React.memo(Select);
export default exportedSelect;

const MaybeTooltip: React.FC<{
  hide: boolean;
  children: React.ReactElement;
  text?: string;
  placement?: TooltipPlacement;
}> = ({ children, hide, text, placement = TooltipPlacement.Bottom }) => {
  if (text) {
    return (
      <Tooltip
        align={TooltipAlignment.Center}
        hide={hide}
        placement={placement}
        text={text}
      >
        {children}
      </Tooltip>
    );
  }

  return children;
};
